import { useState, useRef } from "react";
import { X, Sparkles, Loader2, FileText, Upload, CheckCircle2, ArrowLeft, AlertTriangle } from "lucide-react";
import { BrutalButton } from "@/components/Brutal";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

const DUE = [["nu", "Nu"], ["ochtend", "Ochtend"], ["middag", "Middag"], ["avond", "Avond"], ["vandaag", "Vandaag"]];
const REC = [["", "Eenmalig"], ["dagelijks", "Dagelijks"], ["wekelijks", "Wekelijks"], ["twee_wekelijks", "2-wekelijks"], ["maandelijks", "Maandelijks"]];
const WD = [["", "—"], ["0", "Ma"], ["1", "Di"], ["2", "Wo"], ["3", "Do"], ["4", "Vr"], ["5", "Za"], ["6", "Zo"]];

export default function TaskImportWizard({ open, onClose, onDone, users, groups }) {
  const t = useT();
  const [step, setStep] = useState("paste");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [createGroups, setCreateGroups] = useState(true);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  if (!open) return null;

  const reset = () => { setStep("paste"); setText(""); setItems([]); setResult(null); };
  const close = () => { reset(); onClose(); };

  const toReview = (data) => {
    if (!data.items.length) { toast.error(t("Geen taken herkend. Probeer het duidelijker.")); return; }
    setItems(data.items.map((i) => ({ ...i, action: i.exists ? "overwrite" : "create" })));
    setStep("review");
  };

  const analyseFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/tasks/import/parse-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toReview(data);
    } catch (e) { toast.error(e.response?.data?.detail || t("Analyse van bestand mislukt")); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadPdf = async () => {
    try {
      const { data } = await api.get("/admin/tasks/import/guide", { responseType: "blob" });
      window.open(URL.createObjectURL(data), "_blank");
    } catch { toast.error(t("Kon PDF niet openen")); }
  };

  const analyse = async () => {
    if (!text.trim()) return toast.error(t("Plak eerst je rooster"));
    setBusy(true);
    try {
      const { data } = await api.post("/admin/tasks/import/parse", { text });
      if (!data.items.length) { toast.error(t("Geen taken herkend. Probeer het duidelijker.")); setBusy(false); return; }
      setItems(data.items.map((i) => ({ ...i, action: i.exists ? "overwrite" : "create" })));
      setStep("review");
    } catch (e) { toast.error(e.response?.data?.detail || t("Analyse mislukt")); }
    finally { setBusy(false); }
  };

  const upd = (idx, patch) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const commit = async () => {
    setBusy(true);
    try {
      const payload = {
        create_missing_groups: createGroups,
        items: items.map((i) => ({
          title: i.title, description: i.description || "",
          assignee_id: i.assignee_id || null,
          group_id: i.group_id || null,
          group_name: i.group_id ? null : i.group_name || null,
          due_when: i.due_when, recurrence: i.recurrence || null,
          recurrence_weekday: i.recurrence_weekday === null || i.recurrence_weekday === undefined || i.recurrence_weekday === "" ? null : Number(i.recurrence_weekday),
          action: i.action,
        })),
      };
      const { data } = await api.post("/admin/tasks/import/commit", payload);
      setResult(data);
      setStep("done");
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || t("Import mislukt")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center" onClick={close} data-testid="import-wizard">
      <div className="bg-brand-cream w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl brutal max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 azure-gradient text-white p-4 flex items-center justify-between rounded-t-2xl z-10">
          <div className="flex items-center gap-2">
            {step === "review" && <button data-testid="import-back" onClick={() => setStep("paste")} className="bg-white/20 rounded-full p-1"><ArrowLeft className="w-5 h-5" strokeWidth={2.5} /></button>}
            <div>
              <p className="font-head font-black text-lg leading-none">{t("Rooster importeren")}</p>
              <p className="text-xs opacity-80">{t("AI-wizard · alleen taken")}</p>
            </div>
          </div>
          <button data-testid="import-close" onClick={close} className="bg-white/20 rounded-full p-1.5"><X className="w-5 h-5" strokeWidth={2.5} /></button>
        </div>

        <div className="p-5 space-y-3">
          {step === "paste" && (
            <>
              <p className="text-sm font-semibold text-brand-ink/70">{t("Plak je bestaande rooster (uit Excel, e-mail…). AI zet het om in nette taken die je daarna controleert.")}</p>
              <textarea data-testid="import-text" value={text} onChange={(e) => setText(e.target.value)} rows={9}
                placeholder={"Bijv.\nMaandag: Sanne stofzuigen woonkamer\nElke dag: Daan afwassen na avondeten\nDinsdag: Thomas vuilnis buiten (groep Klusploeg)"}
                className="w-full brutal-sm rounded-xl px-4 py-3 outline-none resize-none text-sm" />
              <div className="flex flex-wrap gap-2">
                <BrutalButton data-testid="import-analyse" onClick={analyse} disabled={busy}>
                  {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" strokeWidth={2.5} />}{t("Analyseer met AI")}
                </BrutalButton>
                <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" data-testid="import-file-input" onChange={(e) => analyseFile(e.target.files?.[0])} />
                <button data-testid="import-upload" onClick={() => fileRef.current?.click()} disabled={busy} className="brutal-sm brutal-press rounded-full px-3 py-2 bg-brand-green font-bold text-sm flex items-center gap-1.5"><Upload className="w-4 h-4" strokeWidth={2.5} />{t("Foto/PDF uploaden")}</button>
                <button data-testid="import-pdf" onClick={downloadPdf} className="brutal-sm brutal-press rounded-full px-3 py-2 bg-white font-bold text-sm flex items-center gap-1.5"><FileText className="w-4 h-4" strokeWidth={2.5} />{t("Uitleg & voorwaarden (PDF)")}</button>
              </div>
              <p className="text-xs text-brand-ink/50 font-semibold">{t("Tip: maak een foto van een rooster op papier of upload een PDF/Excel-export — de AI leest het uit. 📸")}</p>
            </>
          )}

          {step === "review" && (
            <>
              <p className="text-sm font-semibold text-brand-ink/70">{items.length} {t("taken herkend. Controleer en pas aan waar nodig, dan importeren. ✅")}</p>
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <div key={idx} data-testid={`import-item-${idx}`} className={`brutal-sm rounded-xl p-3 ${it.action === "skip" ? "bg-brand-ink/5 opacity-60" : "bg-white"}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <input value={it.title} onChange={(e) => upd(idx, { title: e.target.value })} className="font-head font-black bg-transparent outline-none flex-1 min-w-0" />
                      {it.exists && (
                        <span className="brutal-sm rounded-full px-2 py-0.5 text-[11px] font-bold bg-brand-yellow flex items-center gap-1 shrink-0"><AlertTriangle className="w-3 h-3" strokeWidth={2.5} />{t("Bestaat al")}</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                      <select data-testid={`import-assignee-${idx}`} value={it.assignee_id || ""} onChange={(e) => upd(idx, { assignee_id: e.target.value || null })} className="brutal-sm rounded-lg px-2 py-1.5 text-xs font-bold outline-none">
                        <option value="">{t("Niemand")}</option>
                        {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                      </select>
                      <select data-testid={`import-group-${idx}`} value={it.group_id || (it.group_name ? "__new" : "")} onChange={(e) => { const v = e.target.value; if (v === "__new") upd(idx, { group_id: null }); else upd(idx, { group_id: v || null, group_name: v ? null : it.group_name }); }} className="brutal-sm rounded-lg px-2 py-1.5 text-xs font-bold outline-none">
                        <option value="">{t("Geen groep")}</option>
                        {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                        {it.group_name && !it.group_id && <option value="__new">➕ {t("Nieuw:")} {it.group_name}</option>}
                      </select>
                      <select value={it.due_when} onChange={(e) => upd(idx, { due_when: e.target.value })} className="brutal-sm rounded-lg px-2 py-1.5 text-xs font-bold outline-none">
                        {DUE.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
                      </select>
                      <select value={it.recurrence || ""} onChange={(e) => upd(idx, { recurrence: e.target.value || null })} className="brutal-sm rounded-lg px-2 py-1.5 text-xs font-bold outline-none">
                        {REC.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
                      </select>
                      {it.recurrence === "wekelijks" || it.recurrence === "twee_wekelijks" ? (
                        <select value={it.recurrence_weekday ?? ""} onChange={(e) => upd(idx, { recurrence_weekday: e.target.value === "" ? null : Number(e.target.value) })} className="brutal-sm rounded-lg px-2 py-1.5 text-xs font-bold outline-none">
                          {WD.map(([v, l]) => <option key={v} value={v}>{v === "" ? l : t(l)}</option>)}
                        </select>
                      ) : null}
                    </div>
                    {it.exists && it.action !== "skip" && (
                      <div className="flex gap-1.5 mt-2">
                        <button onClick={() => upd(idx, { action: "overwrite" })} className={`brutal-sm rounded-full px-2.5 py-1 text-[11px] font-bold ${it.action === "overwrite" ? "bg-brand-violet text-white" : "bg-white"}`}>{t("Overschrijven")}</button>
                        <button onClick={() => upd(idx, { action: "skip" })} className={`brutal-sm rounded-full px-2.5 py-1 text-[11px] font-bold bg-white`}>{t("Overslaan")}</button>
                      </div>
                    )}
                    {it.action === "skip" && <button onClick={() => upd(idx, { action: it.exists ? "overwrite" : "create" })} className="text-[11px] font-bold underline mt-1">{t("Toch importeren")}</button>}
                  </div>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm font-bold"><input type="checkbox" checked={createGroups} onChange={(e) => setCreateGroups(e.target.checked)} data-testid="import-create-groups" />{t("Ontbrekende groepen automatisch aanmaken")}</label>
              <BrutalButton data-testid="import-commit" onClick={commit} disabled={busy} className="w-full">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" strokeWidth={2.5} />}{t("Importeer")} {items.filter((i) => i.action !== "skip").length} {t("taken")}
              </BrutalButton>
            </>
          )}

          {step === "done" && result && (
            <div className="text-center py-6 space-y-2" data-testid="import-done">
              <CheckCircle2 className="w-14 h-14 mx-auto text-brand-green" strokeWidth={2.5} />
              <p className="font-head font-black text-2xl">{t("Klaar! 🎉")}</p>
              <p className="text-sm font-semibold text-brand-ink/70">
                {result.created} {t("nieuw")} · {result.updated} {t("bijgewerkt")} · {result.skipped} {t("overgeslagen")}
                {result.groups_created > 0 ? ` · ${result.groups_created} ${t("groep(en) aangemaakt")}` : ""}
              </p>
              <BrutalButton onClick={close} className="mt-2">{t("Naar taken")}</BrutalButton>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
